import { useState, useCallback, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { validateEmail } from "../utils/email";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import {
  LICENSE_AGREEMENT_TEXT,
  buildLicenseAgreementRenderRows,
} from "../utils/licenseAgreement";
import { LicenseAgreementFormattedRows } from "../utils/licenseAgreementRichText";

export const Register = () => {
  const [licenseAccepted, setLicenseAccepted] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();

  const validate = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = "Введите имя";
    } else if (name.trim().length < 2) {
      errors.name = "Имя должно содержать минимум 2 символа";
    }

    if (!email.trim()) {
      errors.email = "Введите email";
    } else if (!validateEmail(email)) {
      errors.email = "Некорректный email";
    }

    if (!password) {
      errors.password = "Введите пароль";
    } else if (password.length < 6) {
      errors.password = "Пароль должен содержать минимум 6 символов";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Подтвердите пароль";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Пароли не совпадают";
    }

    return errors;
  }, [name, email, password, confirmPassword]);

  const isFormValid =
    name.trim().length >= 2 &&
    !!validateEmail(email) &&
    password.length >= 6 &&
    password === confirmPassword;

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const errors = validate();
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) return;
      setError(null);

      axios
        .post("/auth/register", {
          email,
          name: name.trim(),
          password,
        })
        .then(() => {
          setRegistered(true);
        })
        .catch((err: unknown) => {
          setError(getApiErrorMessage(err, "Ошибка при регистрации"));
        });
    },
    [email, name, password, validate]
  );

  if (registered) {
    return (
      <div className="auth-container">
        <div className="auth-floating-wrap">
          <Link to="/" className="auth-home-link auth-home-link--floating" aria-label="На главную">
              <ArrowLeft size={14} />
              <span className="auth-home-link-label">На главную</span>
          </Link>
          <div className="auth-form">
            <h1 className="auth-title">I N A R I</h1>

            <div className="auth-register-success-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => navigate("/login")}
              >
                Войти в систему
              </button>
            </div>

            <div className="footer-links">
              <span className="auth-success-message">
                Аккаунт успешно создан. Проверьте вашу электронную почту для подтверждения.
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!licenseAccepted) {
    const licenseRows = buildLicenseAgreementRenderRows(LICENSE_AGREEMENT_TEXT);
    return (
      <div className="auth-container">
        <div className="auth-form auth-form--license">
          <h1 className="auth-title">I N A R I</h1>
          <p className="register-license-heading">Лицензионное соглашение</p>
          <div className="register-license-scroll">
            <LicenseAgreementFormattedRows
              rows={licenseRows}
              lineClass="register-license-line"
              lineClassSubpoint="register-license-line register-license-line--subpoint"
              linkClass="register-license-link"
            />
          </div>
          <div className="register-license-actions">
            <button
              type="button"
              className="register-license-btn register-license-btn--decline"
              onClick={() => navigate("/login")}
            >
              Отклонить
            </button>
            <button
              type="button"
              className="register-license-btn register-license-btn--accept"
              onClick={() => setLicenseAccepted(true)}
            >
              Принять
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <form className="auth-form" onSubmit={handleSubmit}>
        <h1 className="auth-title">I N A R I</h1>

        <div className="form-group">
          <input
            type="text"
            className={`input${fieldErrors.name ? " input-error" : ""}`}
            placeholder="Введите ваше имя"
            value={name}
            autoFocus
            autoComplete="off"
            onChange={(e) => {
              setFieldErrors((prev) => ({ ...prev, name: "" }));
              setError(null);
              setName(e.target.value);
            }}
          />
          {fieldErrors.name && (
            <div className="input-helper-error">{fieldErrors.name}</div>
          )}
        </div>

        <div className="form-group">
          <input
            type="email"
            className={`input${fieldErrors.email ? " input-error" : ""}`}
            placeholder="Введите ваш email"
            value={email}
            autoComplete="off"
            onChange={(e) => {
              setFieldErrors((prev) => ({ ...prev, email: "" }));
              setError(null);
              setEmail(e.target.value);
            }}
          />
          {fieldErrors.email && (
            <div className="input-helper-error">{fieldErrors.email}</div>
          )}
        </div>

        <div className="form-group">
          <div className="input-password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              className={`input${fieldErrors.password ? " input-error" : ""}`}
              placeholder="Введите пароль"
              value={password}
              autoComplete="new-password"
              onChange={(e) => {
                setFieldErrors((prev) => ({ ...prev, password: "" }));
                setError(null);
                setPassword(e.target.value.replace(/[а-яёА-ЯЁ]/g, ""));
              }}
            />
            {password && (
              <button
                type="button"
                className="input-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            )}
          </div>
          {fieldErrors.password && (
            <div className="input-helper-error">{fieldErrors.password}</div>
          )}
        </div>

        <div className="form-group">
          <div className="input-password-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className={`input${fieldErrors.confirmPassword ? " input-error" : ""}`}
              placeholder="Подтвердите пароль"
              value={confirmPassword}
              autoComplete="new-password"
              onChange={(e) => {
                setFieldErrors((prev) => ({ ...prev, confirmPassword: "" }));
                setError(null);
                setConfirmPassword(
                  e.target.value.replace(/[а-яёА-ЯЁ]/g, "")
                );
              }}
            />
            {confirmPassword && (
              <button
                type="button"
                className="input-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            )}
          </div>
          {fieldErrors.confirmPassword && (
            <div className="input-helper-error">
              {fieldErrors.confirmPassword}
            </div>
          )}
        </div>

        <div className="button-wrapper">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={!isFormValid}
          >
            Продолжить
          </button>
          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="footer-links">
          <Link to="/" className="auth-link auth-link-secondary">
            На главную
          </Link>
          <Link to="/login" className="auth-link">
            Войти в систему
          </Link>
        </div>
      </form>
    </div>
  );
};
